
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DrawingCardSkeleton } from "@/components/DrawingCardSkeleton";
import { FileOutput, Trash2, Brain, Download, Loader2, Image } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExport } from "@/hooks/drawing/use-export";

type Drawing = Tables<"whiteboard_history"> & {
  drawing_analyses?: {
    id: string;
    procedure_type: string;
    analysis_text: string;
    created_at: string;
    drawing_id: string;
    key_points: Json | null;
    status: "pending" | "completed" | "failed";
    updated_at: string;
    user_id: string;
  }[];
};

type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

interface DrawingCardProps {
  drawing: Drawing;
  isImageLoaded: boolean;
  loadingDrawingId: string | null;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  isLastSaved?: boolean;
}

export function DrawingCard({ 
  drawing, 
  isImageLoaded, 
  loadingDrawingId, 
  onLoad, 
  onDelete,
  isLastSaved = false
}: DrawingCardProps) {
  const { isExporting, exportToPDF, exportToJPEG } = useExport();

  const handlePDFExport = async () => {
    if (!drawing.preview_image) return;

    // Get the current analysis from drawing_analyses
    const { data: analysisData } = await supabase
      .from('drawing_analyses')
      .select('analysis_text, procedure_type')
      .eq('drawing_id', drawing.id)
      .eq('is_current', true)
      .maybeSingle();

    await exportToPDF(
      drawing.id,
      drawing.title,
      drawing.preview_image,
      analysisData?.analysis_text || null,
      analysisData?.procedure_type || null
    );
  };

  const handleJPEGExport = async () => {
    if (!drawing.preview_image) return;
    
    // Get analysis text for enhanced JPEG export with analysis
    const { data: analysisData } = await supabase
      .from('drawing_analyses')
      .select('analysis_text')
      .eq('drawing_id', drawing.id)
      .eq('is_current', true)
      .maybeSingle();
    
    // Pass analysis text to the enhanced JPEG export
    await exportToJPEG(
      drawing.id,
      drawing.title,
      drawing.preview_image,
      {
        analysisText: analysisData?.analysis_text || null
      }
    );
  };

  // Determine if the drawing has AI analysis
  const hasAnalysis = drawing.drawing_analyses && drawing.drawing_analyses.length > 0;

  return (
    <div className="group relative rounded-xl border border-border/40 bg-gradient-to-b from-background to-muted/30 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      {/* Preview Image Container */}
      {drawing.preview_image && (
        <div className="relative aspect-video w-full overflow-hidden bg-white/80">
          <div className="absolute inset-0 z-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-40"></div>
          
          {!isImageLoaded ? (
            <DrawingCardSkeleton />
          ) : (
            <img
              src={drawing.preview_image}
              alt={drawing.title}
              className="w-full h-full object-contain z-10 relative p-1"
            />
          )}
          
          {/* Analysis Badge */}
          {hasAnalysis && (
            <div className="absolute top-2 left-2 z-20">
              <Badge 
                variant="secondary" 
                className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 border-blue-500/40 backdrop-blur-sm flex items-center gap-1 px-2 py-0.5 shadow-sm"
              >
                <Brain className="h-3 w-3" />
                <span className="text-[10px] font-medium">AI Analysis</span>
              </Badge>
            </div>
          )}
          
          {/* Card Actions Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 flex items-end justify-end p-2">
            <div className="flex gap-1.5 scale-90 translate-y-2 group-hover:scale-100 group-hover:translate-y-0 transition-transform duration-200">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onLoad(drawing.id)}
                className="bg-white/90 hover:bg-white shadow-sm h-8 w-8 p-0 rounded-full"
                disabled={loadingDrawingId === drawing.id}
                title="Load drawing"
              >
                {loadingDrawingId === drawing.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                ) : (
                  <FileOutput className="h-3.5 w-3.5 text-primary" />
                )}
              </Button>

              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="bg-white/90 hover:bg-white shadow-sm h-8 w-8 p-0 rounded-full"
                    disabled={isExporting}
                    title="Export options"
                  >
                    {isExporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                    ) : (
                      <Download className="h-3.5 w-3.5 text-primary" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-52 bg-white/95 backdrop-blur-sm border border-border/50 shadow-lg p-1 rounded-lg"
                >
                  <DropdownMenuItem 
                    onClick={handleJPEGExport}
                    className="flex items-center h-9 px-2 text-sm cursor-pointer text-foreground/80 hover:text-foreground hover:bg-muted/50 rounded-md"
                  >
                    <Image className="h-4 w-4 mr-2 text-primary/70" />
                    <div>
                      <span className="font-medium">Export as JPEG</span>
                      <p className="text-xs text-muted-foreground">High-quality content-focused image</p>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handlePDFExport}
                    className="flex items-center h-9 px-2 text-sm cursor-pointer text-foreground/80 hover:text-foreground hover:bg-muted/50 rounded-md"
                  >
                    <Download className="h-4 w-4 mr-2 text-primary/70" />
                    <div>
                      <span>Export as PDF</span>
                      <p className="text-xs text-muted-foreground">Document with analysis</p>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="bg-white/90 hover:bg-red-50 hover:text-red-600 shadow-sm h-8 w-8 p-0 rounded-full"
                    title="Delete drawing"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground group-hover:text-red-500" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white/95 backdrop-blur-sm border-border/50">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your drawing
                      and remove it from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(drawing.id)}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-3 space-y-1.5">
        <div className="space-y-0.5">
          <h3 className="font-medium text-sm line-clamp-1 text-foreground/90">
            {drawing.title}
          </h3>
          {drawing.description && (
            <p className="text-xs text-muted-foreground/80 line-clamp-1 group-hover:text-foreground/70 transition-colors">
              {drawing.description}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-1">
          {/* Tags - Hidden until hover */}
          <div className="flex flex-wrap gap-0.5 flex-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {drawing.tags && drawing.tags.length > 0 && drawing.tags.slice(0, 3).map(tag => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-[9px] px-1.5 py-0.5 rounded-md bg-muted/80 group-hover:bg-muted text-muted-foreground/90 group-hover:text-foreground/80 transition-colors"
              >
                {tag}
              </Badge>
            ))}
            {drawing.tags && drawing.tags.length > 3 && (
              <Badge
                variant="secondary"
                className="text-[9px] px-1.5 py-0.5 rounded-md bg-muted/80 group-hover:bg-muted text-muted-foreground/90 group-hover:text-foreground/80 transition-colors"
              >
                +{drawing.tags.length - 3}
              </Badge>
            )}
          </div>

          {/* Timestamp - Only show if not in Last Saved section */}
          {!isLastSaved && (
            <p className="text-[10px] text-muted-foreground/70 whitespace-nowrap group-hover:text-muted-foreground/90 transition-colors">
              {format(new Date(drawing.created_at), "MMM d, yyyy")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
